// @flow
/**
 * Builds the PUT .../applications/{id}/applicants payload for a pre-move-in
 * Change Head of Household (applicant path). The legacy Edit Household
 * page's own primary-swap (EditHouseholdForm/index.js's onPrimaryChange) is
 * the model this mirrors: the incoming HoH's Relation goes blank (a HoH has
 * no relationship-to-self) and, on Affordable, is auto-set to "Head of
 * Household"; the outgoing HoH's Relation/Affordable Relation become
 * whatever this modal collected for them.
 *
 * The resident-path mutation (useChangeHeadOfHouseholdMutation) can't be
 * reused here -- it resolves the new head by residentId and requires a
 * Resident row with status 'Current', neither of which exists pre-move-in.
 * updateApplicants is the only route that both promotes an applicant to
 * primary AND carries the same completed-certification-reopen behavior the
 * legacy Edit Household page already triggers for this exact change.
 */
import { getExistingApplicantAdults } from '../../ResidentProfile/MiniProfileBridge/buildSavePayload';

const HEAD_OF_HOUSEHOLD_AFFORDABLE_RELATIONSHIP_TEXT = 'Head of Household';

export const buildChangeApplicantHeadOfHouseholdPayload = ({
  applicants,
  newHeadOfHouseholdId,
  formerHeadOfHouseholdId,
  formerRelationshipId,
  isAffordable,
  formerAffordableRelationshipId,
  affordableRelationshipOptions = [],
}: {
  applicants: Array<Object>,
  newHeadOfHouseholdId: string,
  formerHeadOfHouseholdId: ?string,
  formerRelationshipId: string,
  isAffordable: boolean,
  formerAffordableRelationshipId?: string,
  affordableRelationshipOptions?: Array<Object>,
}): Object => {
  const headOfHouseholdAffordableRelationshipId =
    affordableRelationshipOptions.find(
      (option) =>
        option.text === HEAD_OF_HOUSEHOLD_AFFORDABLE_RELATIONSHIP_TEXT,
    )?.value;

  const adults = getExistingApplicantAdults(applicants).map((adult) => {
    if (adult.id === newHeadOfHouseholdId) {
      return {
        ...adult,
        isProspect: true,
        relationship: null,
        ...(isAffordable
          ? {
              affordableRelationship:
                headOfHouseholdAffordableRelationshipId ?? null,
            }
          : {}),
      };
    }
    if (adult.id === formerHeadOfHouseholdId) {
      return {
        ...adult,
        isProspect: false,
        relationship: formerRelationshipId,
        ...(isAffordable
          ? { affordableRelationship: formerAffordableRelationshipId }
          : {}),
      };
    }
    return adult;
  });

  return {
    // Drives updateApplicants' updateMainCustomer promotion (Applications.js)
    // -- the isProspect flags above only satisfy validateApplicationCreate's
    // own payload-level check, they don't persist the promotion themselves.
    primaryApplicantId: newHeadOfHouseholdId,
    adults,
    // updateApplicants only touches whatever is present in each array (see
    // getExistingApplicantAdults's own comment) -- minors/pets are left
    // alone, same convention as the applicant Add flow.
    minors: [],
    pets: [],
  };
};

export default buildChangeApplicantHeadOfHouseholdPayload;
